/**
 * Data Panel › Seasonality's pure half: the window picked on the Seasonality
 * screen, shared with the panel, and how its numbers read.
 *
 * Module scope with subscribers (`lib/symbol-index.ts`' shape): the screen and
 * the panel are siblings in the dock, and a row click on one has to reach the
 * other without a prop path through `App`.
 */

import type { SeasWindow } from "../api/seasonality";

export interface PickedWindow {
  symbol: string;
  basis: "d" | "w" | "m";
  years: string;
  from: string;
  len: number;
  dir: "L" | "S";
}

let picked: PickedWindow | null = null;
const listeners = new Set<() => void>();

/** A row clicked on the Seasonality screen — the window the panel describes. */
export function pickWindow(w: SeasWindow, basis: string, years: string): void {
  if (!w.startDate) return;
  picked = {
    symbol: w.symbol,
    basis: (basis === "w" || basis === "m" ? basis : "d") as PickedWindow["basis"],
    years,
    from: w.startDate,
    len: w.len,
    dir: w.dir,
  };
  for (const l of listeners) l();
}

/** The picked window, if it is for this symbol. */
export function pickedFor(symbol: string): PickedWindow | null {
  return picked && picked.symbol.toUpperCase() === symbol.toUpperCase() ? picked : null;
}

export function onPicked(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const UNIT_LABEL: Record<string, string> = { d: "td", w: "w", m: "m" };

/** `+18.40%`, `−4.20%` — signed, two places. */
export function signedPct(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "";
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(2)}%`;
}

/** A year × month cell's tint: green or red, stronger with size, capped at 10%. */
export function cellTint(v: number | null | undefined): string | undefined {
  if (v === null || v === undefined || !Number.isFinite(v) || v === 0) return undefined;
  const a = Math.min(0.75, Math.abs(v) / 10 * 0.75);
  return v > 0 ? `rgba(46, 160, 67, ${a.toFixed(2)})` : `rgba(248, 81, 73, ${a.toFixed(2)})`;
}

/** Whether a year went the window's way. */
export function wentItsWay(ret: number, dir: "L" | "S"): boolean {
  return dir === "L" ? ret > 0 : ret < 0;
}
